'use client';

import { forwardRef, type ReactNode, type SelectHTMLAttributes } from 'react';
import {
  useFormContext,
  type ControllerRenderProps,
  type FieldPathByValue,
  type FieldValues,
} from 'react-hook-form';

import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import { FormRow } from '@eliadmin/core/src/components/dlz/forms/form-row';
import { PasswordInput } from '@eliadmin/core/src/components/dlz/forms/password-input';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  FormControl,
  FormField,
  FormItem,
  useFormField,
} from '@eliadmin/core/src/components/ui/form';
import { Input, type InputProps } from '@eliadmin/core/src/components/ui/input';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type RowProps = {
  label: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  /** Classes of the whole row (grid placement). */
  className?: string;
};

type InputPassThrough = Pick<
  InputProps,
  'type' | 'inputMode' | 'autoComplete' | 'placeholder' | 'after' | 'classNames' | 'disabled'
>;

export type TextFieldProps<T extends FieldValues> = RowProps &
  InputPassThrough & { name: FieldPathByValue<T, string> };

export type PasswordFieldProps<T extends FieldValues> = RowProps &
  Pick<InputProps, 'autoComplete' | 'placeholder'> & { name: FieldPathByValue<T, string> };

export type CountryFieldProps<T extends FieldValues> = Pick<RowProps, 'label' | 'required'> & {
  name: FieldPathByValue<T, string>;
  countries: Country[];
  autoComplete?: string;
};

export type CheckboxFieldProps<T extends FieldValues> = {
  name: FieldPathByValue<T, boolean>;
  required?: boolean;
  children: ReactNode;
  className?: string;
};

/** Figma „Input“: label row, 52 px field, helper or error – wired to the surrounding form. */
function TextField({
  name,
  label,
  required,
  hint,
  className,
  ...input
}: TextFieldProps<FieldValues>) {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormRow label={label} required={required} hint={hint} className={className}>
          <Input
            {...field}
            value={field.value ?? ''}
            {...input}
            aria-required={required || undefined}
          />
        </FormRow>
      )}
    />
  );
}

function PasswordField({
  name,
  label,
  required,
  hint,
  className,
  ...input
}: PasswordFieldProps<FieldValues>) {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormRow label={label} required={required} hint={hint} className={className}>
          <PasswordInput {...field} {...input} aria-required={required || undefined} />
        </FormRow>
      )}
    />
  );
}

type CountrySelectProps = SelectHTMLAttributes<HTMLSelectElement> & { countries: Country[] };

/**
 * Figma „Input“ with the caret (60260:28457): the field of the DLZ `Input` around a native select,
 * which keeps the long country list searchable by typing and opens the system picker on phones.
 */
const CountrySelect = forwardRef<HTMLSelectElement, CountrySelectProps>(
  ({ countries, ...props }, ref) => (
    <div
      className={cn(
        'dlz-body-m relative flex h-[52px] w-full items-center rounded-[18px] border border-[#E2E4E7] bg-white text-[#0F1012] transition-[border-color,box-shadow]',
        'focus-within:border-[#0F1012] focus-within:shadow-[inset_0_0_0_1px_#0F1012] hover:border-[#9096A0]',
        'has-[:disabled]:cursor-not-allowed has-[:disabled]:border-[#E2E4E7] has-[:disabled]:bg-[#ECEDEF] has-[:disabled]:text-[#9096A0]',
        props['aria-invalid'] &&
          'border-[#D7191F] shadow-[inset_0_0_0_1px_#D7191F] focus-within:border-[#D7191F] focus-within:shadow-[inset_0_0_0_1px_#D7191F] hover:border-[#D7191F]',
      )}
    >
      <select
        ref={ref}
        className="h-full w-full min-w-0 cursor-pointer appearance-none rounded-[inherit] bg-transparent pl-4 pr-11 outline-none disabled:cursor-not-allowed"
        {...props}
      >
        {countries.map(country => (
          <option key={country.id} value={country.id}>
            {country.name}
          </option>
        ))}
      </select>
      <Icon
        name="caret-down-fill"
        size={14}
        className="pointer-events-none absolute right-4 text-[#0F1012]"
      />
    </div>
  ),
);
CountrySelect.displayName = 'CountrySelect';

function CountryField({
  name,
  label,
  required,
  countries,
  autoComplete,
}: CountryFieldProps<FieldValues>) {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormRow label={label} required={required}>
          <CountrySelect
            countries={countries}
            autoComplete={autoComplete}
            aria-required={required || undefined}
            {...field}
            value={field.value ?? ''}
          />
        </FormRow>
      )}
    />
  );
}

function CheckboxBody({
  field,
  required,
  children,
}: {
  field: ControllerRenderProps<FieldValues, string>;
  required?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslations();
  const { error, formItemId, formMessageId } = useFormField();
  const message = error?.message ? t(String(error.message)) : null;

  return (
    <>
      <div className="flex items-center gap-3">
        <FormControl>
          <Checkbox
            ref={field.ref}
            name={field.name}
            checked={!!field.value}
            aria-required={required}
            onBlur={field.onBlur}
            onCheckedChange={checked => field.onChange(checked === true)}
          />
        </FormControl>
        <label htmlFor={formItemId} className="dlz-body-m cursor-pointer text-[#0F1012]">
          {children}
        </label>
      </div>
      {message ? (
        <p id={formMessageId} role="alert" className="dlz-caption text-[#D7191F]">
          {message}
        </p>
      ) : null}
    </>
  );
}

/** Figma „Checkbox“: 24 px box, 12 px gap, label DLZ/Body/M centred on the box (two lines on phones). */
function CheckboxField({ name, required, children, className }: CheckboxFieldProps<FieldValues>) {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('flex flex-col gap-1', className)}>
          <CheckboxBody field={field} required={required}>
            {children}
          </CheckboxBody>
        </FormItem>
      )}
    />
  );
}

/**
 * The DLZ form fields typed for one form: `const { TextField } = formFields<MyFormInput>()` – the
 * `name` props then only accept that form's string / boolean paths.
 */
export function formFields<T extends FieldValues>() {
  return {
    TextField: TextField as (props: TextFieldProps<T>) => ReactNode,
    PasswordField: PasswordField as (props: PasswordFieldProps<T>) => ReactNode,
    CountryField: CountryField as (props: CountryFieldProps<T>) => ReactNode,
    CheckboxField: CheckboxField as (props: CheckboxFieldProps<T>) => ReactNode,
  };
}
